import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiPackage, 
  FiTruck, 
  FiCheckCircle, 
  FiClock, 
  FiAlertCircle,
  FiRefreshCw,
  FiShoppingBag,
  FiDollarSign,
  FiCalendar,
  FiLogIn,
  FiTag,
  FiCreditCard,
  FiInfo
} from 'react-icons/fi';
import { FaPhoneAlt } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';

const API_BASE_URL = "https://server.havbit.in/api";

export default function OrdersPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login", { state: { from: "/orders" } });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchOrders();
    }
  }, [isAuthenticated]);

  const fetchOrders = async () => {
    try {
      setLoading(true);

      if (!isAuthenticated) throw new Error("Please login to view orders");

      const token = localStorage.getItem('token') || '';

      let backendOrders = [];
      try {
        const response = await fetch(`${API_BASE_URL}/customer/orders`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success) backendOrders = data.orders || data.data || [];
        }
      } catch (backendError) {
        console.log('Backend fetch failed:', backendError.message);
      }

      const allLocalOrders = JSON.parse(localStorage.getItem('user_orders') || '[]');
      const userLocalOrders = allLocalOrders.filter(order =>
        order.customer === user?._id || order.customerId === user?._id
      );

      const allOrdersMap = new Map();
      backendOrders.forEach(order => { if (order._id) allOrdersMap.set(order._id, order); });
      userLocalOrders.forEach(order => { if (order._id && !allOrdersMap.has(order._id)) allOrdersMap.set(order._id, order); });

      const mergedOrders = Array.from(allOrdersMap.values()).sort((a, b) =>
        new Date(b.createdAt || b.createdDate || 0) - new Date(a.createdAt || a.createdDate || 0)
      );

      setOrders(mergedOrders);
    } catch (error) {
      console.error('❌ Error fetching orders:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Delivered':   return 'bg-green-100 text-green-800';
      case 'Shipped':     return 'bg-blue-100 text-blue-800';
      case 'Confirmed':
      case 'Processing':  return 'bg-yellow-100 text-yellow-800';
      case 'Cancelled':   return 'bg-gray-100 text-gray-800';
      default:            return 'bg-red-100 text-red-800';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'Success':
      case 'Completed': return 'text-green-600';
      case 'Pending':   return 'text-yellow-600';
      case 'Failed':    return 'text-red-600';
      default:          return 'text-gray-600';
    }
  };

  /* ─────────────────────────────────────────────────────────
     DELIVERY FEE LOGIC
     COD  → Rs.30 delivery charge
     Online (razorpay) → FREE delivery
  ───────────────────────────────────────────────────────── */
  const getDeliveryFee = (order) => {
    // If backend already sent deliveryFee, use it
    if (order.deliveryFee !== undefined && order.deliveryFee !== null) {
      return parseFloat(order.deliveryFee);
    }
    // Otherwise calculate based on payment method
    return order.paymentMethod === 'cod' ? 30 : 0;
  };

  /* ─────────────────────────────────────────────────────────
     ORDER TOTAL CALCULATION
  ───────────────────────────────────────────────────────── */
  const calculateOrderTotal = (order) => {
    // If backend sent final amount, trust it
    if (order.finalAmount || order.totalPayable || order.amount) {
      return parseFloat(order.finalAmount || order.totalPayable || order.amount || 0);
    }

    // Recalculate with correct delivery fee logic
    let subtotal = 0;
    if (order.orderItems?.length > 0) {
      subtotal = order.orderItems.reduce((total, item) => {
        return total + (parseFloat(item.price) || 0) * (parseInt(item.qty) || 1);
      }, 0);
    }

    const deliveryFee = getDeliveryFee(order);
    const platformFee = 0;
    // const gst = subtotal * 0.05;
    const couponDiscount = parseFloat(order.couponDiscount || 0);
    const total = subtotal + deliveryFee + platformFee  - couponDiscount;
    return parseFloat(total.toFixed(2));
  };

  /* ─────────────── GUARDS ─────────────── */
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <FiLogIn className="text-red-600 text-3xl" />
          </div>
          <h2 className="text-2xl font-bold mb-2 text-gray-900">Login Required</h2>
          <p className="text-gray-600 mb-4">Please login to view your orders.</p>
          <button
            onClick={() => navigate("/login", { state: { from: "/orders" } })}
            className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors font-semibold w-full"
          >
            Login Now
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-red-600 mb-4" />
        <p className="text-gray-600">Loading your orders...</p>
      </div>
    );
  }

  /* ─────────────── RENDER ─────────────── */
  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">

        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <FiShoppingBag />
            My Orders
          </h1>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">
              Welcome, <span className="font-bold">{user?.name?.split(' ')[0]}</span>
            </span>
            <button
              onClick={fetchOrders}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <FiRefreshCw />
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-red-600 flex items-center gap-2">
              <FiAlertCircle />
              {error}
            </p>
          </div>
        )}

        {/* Empty State */}
        {orders.length === 0 ? (
          <div className="bg-white rounded-2xl shadow border border-gray-200 p-12 text-center">
            <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-6">
              <FiPackage className="text-gray-400 text-3xl" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Orders Yet</h2>
            <p className="text-gray-600 mb-6">You haven't placed any orders yet.</p>
            <button
              onClick={() => navigate('/products')}
              className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold"
            >
              Start Shopping
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => {
              const orderTotal   = calculateOrderTotal(order);
              const deliveryFee  = getDeliveryFee(order);
              const itemCount    = order.orderItems?.length || 0;
              const hasCoupon    = order.couponCode || order.couponDiscount > 0;
              const isCOD        = order.paymentMethod === 'cod';
              const subtotal     = parseFloat(order.subtotal) ||
                (order.orderItems?.reduce((s, i) => s + (parseFloat(i.price) || 0) * (parseInt(i.qty) || 1), 0) || 0);

              return (
                <div
                  key={order._id}
                  className="bg-white rounded-2xl shadow border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/order/${order._id}`)}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      {/* Order ID + badges */}
                      <div className="flex items-center flex-wrap gap-3 mb-3">
                        <h3 className="text-lg font-bold text-gray-900">
                          Order #{order._id?.slice(-8).toUpperCase()}
                        </h3>

                        {/* COD badge */}
                        {isCOD ? (
                          <span className="flex items-center gap-1 text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded font-semibold">
                            <FaPhoneAlt size={9} />
                            COD
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded font-semibold">
                            <FiCreditCard size={10} />
                            Online
                          </span>
                        )}

                        {/* Coupon badge */}
                        {hasCoupon && (
                          <span className="flex items-center gap-1 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                            <FiTag size={10} />
                            Coupon Applied
                          </span>
                        )}
                      </div>

                      {/* Date + Amount + Status */}
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <FiCalendar size={13} />
                          <span>
                            {new Date(order.createdAt).toLocaleDateString('en-IN', {
                              day: 'numeric', month: 'short', year: 'numeric'
                            })}
                          </span>
                        </div>

                        <div className="flex items-center gap-1">
                          <span className="font-bold text-gray-900">₹{orderTotal.toFixed(2)}</span>
                          {hasCoupon && (
                            <span className="text-xs text-green-600 ml-1">
                              (Saved ₹{(order.couponDiscount || 0).toFixed(2)})
                            </span>
                          )}
                        </div>

                        <span className={`px-2 py-1 rounded text-xs font-bold ${getStatusColor(order.orderStatus)}`}>
                          {order.orderStatus || 'Pending'}
                        </span>
                      </div>

                      {/* Payment info */}
                      <div className="mt-2 text-sm flex items-center gap-2 flex-wrap">
                        <span className="text-gray-500">Payment:</span>
                        <span className={`font-medium ${getPaymentStatusColor(order.paymentStatus)}`}>
                          {isCOD ? 'Cash on Delivery' :
                           order.paymentMethod === 'razorpay' ? 'Razorpay (Online)' :
                           order.paymentMethod || 'N/A'}
                          {' '}({order.paymentStatus || 'Pending'})
                        </span>

                        {/* COD delivery charge notice */}
                        {isCOD && (
                          <span className="flex items-center gap-1 text-xs text-orange-600 bg-orange-50 border border-orange-200 px-2 py-0.5 rounded">
                            <FiInfo size={10} />
                            ₹30 delivery charge
                          </span>
                        )}

                        {/* Online free delivery notice */}
                        {!isCOD && order.paymentMethod === 'razorpay' && (
                          <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 border border-green-200 px-2 py-0.5 rounded">
                            <FiCheckCircle size={10} />
                            Free delivery
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Right side */}
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 rounded-lg font-bold text-sm ${getStatusColor(order.orderStatus)}`}>
                        {order.orderStatus || 'Pending'}
                      </span>
                      <button className="text-red-600 hover:text-red-700 font-semibold text-sm">
                        View Details →
                      </button>
                    </div>
                  </div>

                  {/* Order Items Preview */}
                  {order.orderItems?.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <FiPackage className="text-gray-400" size={14} />
                        <span className="text-sm text-gray-500">
                          {itemCount} item{itemCount > 1 ? 's' : ''}
                        </span>

                        {/* Delivery fee badge */}
                        {deliveryFee === 0 ? (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-medium">
                            ✓ Free Delivery
                          </span>
                        ) : (
                          <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded font-medium">
                            ₹{deliveryFee} Delivery (COD)
                          </span>
                        )}
                      </div>

                      {/* Product name pills */}
                      <div className="flex flex-wrap gap-2">
                        {order.orderItems.slice(0, 3).map((item, idx) => (
                          <div key={idx} className="text-sm bg-gray-50 px-3 py-1 rounded border border-gray-100">
                            {item.productName} × {item.qty}
                          </div>
                        ))}
                        {itemCount > 3 && (
                          <div className="text-sm text-gray-500 self-center">+{itemCount - 3} more</div>
                        )}
                      </div>

                      {/* Order mini summary */}
                      <div className="mt-3 pt-2 border-t border-dashed border-gray-100 text-xs text-gray-500 flex flex-wrap justify-between gap-2">
                        <span>Subtotal: ₹{subtotal.toFixed(2)}</span>
                        <span className={deliveryFee === 0 ? 'text-green-600 font-medium' : 'text-orange-600 font-medium'}>
                          Delivery: {deliveryFee === 0 ? 'FREE' : `₹${deliveryFee}`}
                        </span>
                        <span className="font-bold text-gray-700">Total: ₹{orderTotal.toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}